import { isSensitiveInput, type RedactionConfig } from './redaction';

/**
 * Ausführung der Remote-Control-Befehle im DOM (Spec Kap. 5 Punkt 7). Jede Funktion liefert
 * `null` bei Erfolg oder den Ablehnungsgrund für `control.rejected`.
 */

export type RejectReason =
  | 'not_granted'
  | 'not_found'
  | 'redacted'
  | 'unsupported_key'
  | 'unsupported_target'
  | 'unsupported'
  | 'readonly'
  | 'forbidden';

/** Erlaubte Tasten für `control.key`; Text läuft über `control.input`. */
export const ALLOWED_KEYS = new Set([
  'Enter',
  'Escape',
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Backspace',
]);

/** `input`-Typen ohne Textwert: `control.input` wird abgelehnt (Klick nutzen). */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'file',
  'hidden',
  'image',
  'radio',
  'reset',
  'submit',
]);

export interface Modifiers {
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
}

/** Fenster des Elements (auch in same-origin-iframes), mit dessen Event-Konstruktoren. */
type View = Window & typeof globalThis;
function viewOf(el: Element): View {
  return (el.ownerDocument.defaultView ?? window) as View;
}

function isElement(n: Node | null): n is Element {
  return !!n && n.nodeType === 1;
}

function isContentEditable(el: Element): boolean {
  const ce = (el as HTMLElement).isContentEditable;
  if (typeof ce === 'boolean') return ce;
  const attr = el.closest('[contenteditable]')?.getAttribute('contenteditable');
  return attr !== undefined && attr !== null && attr.toLowerCase() !== 'false';
}

function focusEl(el: Element): void {
  const f = (el as HTMLElement).focus;
  if (typeof f !== 'function') return;
  try {
    f.call(el, { preventScroll: true });
  } catch {
    // nicht fokussierbar
  }
}

/**
 * Setzt `value` über den nativen Setter des Prototyps. React (und andere Frameworks) legen auf
 * der Instanz einen eigenen `value`-Setter an, der den Wert nur „merkt“; ein danach gefeuertes
 * `input`-Event erkennt die Änderung nur, wenn der Wert am Instanz-Setter vorbei gesetzt wurde.
 */
function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  let proto: object | null = Object.getPrototypeOf(el);
  while (proto) {
    const desc = Object.getOwnPropertyDescriptor(proto, 'value');
    if (desc?.set) {
      desc.set.call(el, value);
      return;
    }
    proto = Object.getPrototypeOf(proto);
  }
  el.value = value;
}

function dispatchInput(el: Element, value: string): void {
  const win = viewOf(el);
  const init = { bubbles: true, cancelable: false, composed: true };
  let ev: Event;
  if (typeof win.InputEvent === 'function') {
    ev = new win.InputEvent('input', { ...init, inputType: 'insertReplacementText', data: value });
  } else {
    ev = new win.Event('input', init);
  }
  el.dispatchEvent(ev);
}

// --------------------------------------------------------------------- click

export function runClick(target: Node | null): RejectReason | null {
  if (!isElement(target) || !target.isConnected) return 'not_found';
  const win = viewOf(target);
  const rect = target.getBoundingClientRect();
  const base = {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: win,
    clientX: rect.left + rect.width / 2,
    clientY: rect.top + rect.height / 2,
    button: 0,
  };
  const pointer = (type: string, buttons: number): Event => {
    const init = { ...base, buttons, pointerId: 1, pointerType: 'mouse', isPrimary: true };
    return typeof win.PointerEvent === 'function'
      ? new win.PointerEvent(type, init)
      : new win.MouseEvent(type, init);
  };
  focusEl(target);
  target.dispatchEvent(pointer('pointerdown', 1));
  target.dispatchEvent(new win.MouseEvent('mousedown', { ...base, buttons: 1 }));
  target.dispatchEvent(pointer('pointerup', 0));
  target.dispatchEvent(new win.MouseEvent('mouseup', { ...base, buttons: 0 }));
  // Ein synthetischer click löst auch die Aktivierung aus (Link folgen, Formular absenden).
  target.dispatchEvent(new win.MouseEvent('click', { ...base, buttons: 0, detail: 1 }));
  return null;
}

// --------------------------------------------------------------------- input

export function runInput(
  target: Node | null,
  value: string,
  cfg: RedactionConfig,
): RejectReason | null {
  if (!isElement(target) || !target.isConnected) return 'not_found';
  // Redaction zuerst: auch für Container in `.relyo-redact`, deren Inhalt geblockt ist.
  if (isSensitiveInput(target, cfg)) return 'redacted';
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea') {
    const field = target as HTMLInputElement | HTMLTextAreaElement;
    if (tag === 'input' && NON_TEXT_INPUT_TYPES.has((field as HTMLInputElement).type)) {
      return 'unsupported_target';
    }
    if (field.disabled || field.readOnly) return 'readonly';
    focusEl(field);
    setNativeValue(field, value);
  } else if (isContentEditable(target)) {
    focusEl(target);
    target.textContent = value;
  } else {
    return 'unsupported_target';
  }
  dispatchInput(target, value);
  target.dispatchEvent(new (viewOf(target).Event)('change', { bubbles: true }));
  return null;
}

// -------------------------------------------------------------------- scroll

export function runScroll(
  target: Node | null | undefined,
  x: number,
  y: number,
): RejectReason | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return 'unsupported';
  const opts: ScrollToOptions = { left: x, top: y, behavior: 'auto' };
  // Ohne nodeId bzw. für Dokument/Root-Element: Fenster scrollen.
  if (target === undefined) {
    window.scrollTo(opts);
    return null;
  }
  if (!target || !target.isConnected) return 'not_found';
  if (target.nodeType === 9) {
    ((target as Document).defaultView ?? window).scrollTo(opts);
    return null;
  }
  if (!isElement(target)) return 'not_found';
  const doc = target.ownerDocument;
  if (target === doc.documentElement || target === doc.body) {
    viewOf(target).scrollTo(opts);
    return null;
  }
  if (typeof target.scrollTo === 'function') target.scrollTo(opts);
  else {
    target.scrollLeft = x;
    target.scrollTop = y;
  }
  return null;
}

// ----------------------------------------------------------------------- key

/**
 * `keydown`/`keyup` auf das aktive Element. Synthetische Tastenereignisse lösen keine
 * Browser-Standardaktion aus; nur Enter in einem Formularfeld wird per `requestSubmit()`
 * nachgebildet (wenn `keydown` nicht abgebrochen wurde).
 */
export function runKey(
  key: string,
  code: string,
  modifiers: Modifiers,
  isOwnNode: (n: Node) => boolean,
): RejectReason | null {
  if (!ALLOWED_KEYS.has(key)) return 'unsupported_key';
  let target: Element = document.activeElement ?? document.body;
  // Fokus in der SDK-Oberfläche: Tasten nicht dorthin schicken (Agent darf sie nicht bedienen).
  if (isOwnNode(target)) target = document.body;
  const init: KeyboardEventInit = {
    key,
    code: typeof code === 'string' ? code : '',
    bubbles: true,
    cancelable: true,
    composed: true,
    altKey: modifiers?.alt === true,
    ctrlKey: modifiers?.ctrl === true,
    metaKey: modifiers?.meta === true,
    shiftKey: modifiers?.shift === true,
  };
  const win = viewOf(target);
  const down = new win.KeyboardEvent('keydown', init);
  const proceed = target.dispatchEvent(down);
  if (proceed && key === 'Enter' && target.tagName.toLowerCase() === 'input') {
    const form = (target as HTMLInputElement).form;
    if (form && typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit();
      } catch {
        // Ungültiges Formular o. Ä.: Browser würde ebenfalls nicht absenden.
      }
    }
  }
  target.dispatchEvent(new win.KeyboardEvent('keyup', init));
  return null;
}
